// One picker for a long list (Spencer, 2026-10-16, on the laundry room's custom wine dividers: "When trying to pick a
// BOM or route item, it is very frustrating to find the one that I want by going through a long list. Starting to
// type the item doesn't really help because it doesn't jump in cohesive groups."). A box showing the pick opens a
// panel with a search box and the options under their group headings (an operation's category, a material's
// category and subcategory, a catalog item's family); typing keeps the options whose name, group or detail carry
// every word typed, headings and all; the arrows move, Enter takes the lit one, Escape closes. The panel is drawn on
// the page like the Info popover, so a table cell or a scrolling list cannot clip it, and it stays open while its
// own list scrolls. A login that cannot write sees the pick as words.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from '../lib/session';
import { Value } from './kit';

export interface PickOption {
  id: string;
  label: string;
  /** The heading the option sits under; options are listed under their headings in the order given. */
  group?: string | null;
  /** What is shown after the name, smaller: minutes and unit, price and unit, how many items. */
  detail?: string | null;
}

const EDGE = 8;
const PANEL = 400;

export function Pick({ value, options, onPick, placeholder, title, disabled, style }: {
  value: string;
  options: PickOption[];
  onPick: (id: string) => void;
  placeholder?: string;
  /** Said on hover: what the list is kept to ("Only pulls and knobs"). */
  title?: string;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  const { canWrite } = useSession();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [lit, setLit] = useState(0);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number; width: number; height: number }>({ top: 0, left: 0, width: 0, height: PANEL });
  const [moved, setMoved] = useState(0);
  const chosen = options.find((o) => o.id === value);

  // every word typed must be somewhere in the name, the heading or the detail; the headings stay with their options
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = useMemo(() => {
    const groups: { name: string; items: PickOption[] }[] = [];
    const byName = new Map<string, { name: string; items: PickOption[] }>();
    for (const o of options) {
      const hay = `${o.label} ${o.group ?? ''} ${o.detail ?? ''}`.toLowerCase();
      if (!words.every((w) => hay.includes(w))) continue;
      const name = o.group ?? '';
      let g = byName.get(name);
      if (!g) {
        g = { name, items: [] };
        byName.set(name, g);
        groups.push(g);
      }
      g.items.push(o);
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, text]);
  const flat = useMemo(() => shown.flatMap((g) => g.items), [shown]);

  // under the box, the box's width or wider, kept inside the window; above it when there is more room there
  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const b = btn.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(Math.max(b.width, 320), vw - 2 * EDGE);
    const left = Math.max(EDGE, Math.min(b.left, vw - EDGE - width));
    const below = vh - EDGE - (b.bottom + 4);
    const above = b.top - 4 - EDGE;
    if (below >= Math.min(PANEL, 260) || below >= above) setAt({ top: b.bottom + 4, left, width, height: Math.min(PANEL, below) });
    else {
      const height = Math.min(PANEL, above);
      setAt({ top: b.top - 4 - height, left, width, height });
    }
  }, [open, moved]);

  useEffect(() => {
    if (!open) return;
    setText('');
    setLit(0);
    // the search box takes the keyboard as the panel opens
    const t = window.setTimeout(() => box.current?.focus(), 0);
    const outside = (e: MouseEvent | TouchEvent) => {
      const n = e.target as Node;
      if (!btn.current?.contains(n) && !pop.current?.contains(n)) setOpen(false);
    };
    // the page moving under the panel moves the panel; the panel's own list scrolls on its own
    const scrolled = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return;
      setMoved((m) => m + 1);
    };
    const resized = () => setMoved((m) => m + 1);
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    document.addEventListener('scroll', scrolled, true);
    window.addEventListener('resize', resized);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      document.removeEventListener('scroll', scrolled, true);
      window.removeEventListener('resize', resized);
    };
  }, [open]);

  // the lit option stays in view as the arrows move it
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-i="${lit}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, lit]);

  if (!canWrite) return <Value style={style}>{chosen?.label ?? value}</Value>;

  const pick = (o: PickOption | undefined) => {
    if (!o) return;
    setOpen(false);
    onPick(o.id);
  };
  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      btn.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setLit((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLit((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(flat[lit] ?? flat[0]);
    }
  };

  let n = -1;
  return (
    <>
      <button
        type="button"
        ref={btn}
        className={`pick${chosen ? '' : ' blank'}`}
        style={style}
        disabled={disabled}
        title={title ?? (chosen && chosen.label.length > 24 ? chosen.label : undefined)}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <span className="pick-text">{chosen ? chosen.label : placeholder ?? 'Pick…'}</span>
        <span className="pick-arrow" aria-hidden>
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div ref={pop} className="pick-pop" role="dialog" style={{ top: at.top, left: at.left, width: at.width, maxHeight: at.height }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
            <input
              ref={box}
              type="search"
              className="read"
              placeholder={`Type to find · ${options.length} to pick from`}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setLit(0);
              }}
              onKeyDown={key}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <div ref={list} className="pick-list" role="listbox">
              {flat.length === 0 && <div className="pick-none">Nothing matches “{text}”.</div>}
              {shown.map((g) => (
                <div key={g.name || '—'} className="pick-group">
                  {g.name && <div className="pick-head">{g.name}</div>}
                  {g.items.map((o) => {
                    n += 1;
                    const i = n;
                    return (
                      <div
                        key={o.id}
                        data-i={i}
                        role="option"
                        aria-selected={o.id === value}
                        className={`pick-opt${i === lit ? ' lit' : ''}${o.id === value ? ' now' : ''}`}
                        onMouseEnter={() => setLit(i)}
                        onClick={() => pick(o)}
                      >
                        <span className="pick-label">{o.label}</span>
                        {o.detail && <small className="pick-detail">{o.detail}</small>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
